import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {BuyerPaymentButton} from '@/features/buyers/components';
import {usePaymentsQuery} from '@/features/buyers/hooks';
import {getAssetFeedSubtitle, isNonTerminalInstruction} from '@/features/buyers/utils';
import {LEGACY_ASSET_PARAM, PURCHASE_PARAM} from '@/features/buyers/view/hooks';
import {
  useTrustAccountPermissions,
  useTrustTypeStrategy
} from '@/features/trust-accounts/providers';

import {ActivityFeedRowLayout} from '../ActivityFeedRowLayout';
import {BuyerNamesSummary} from './BuyerNamesSummary';
import {castMetadata, readAssetSummary, readBuyerNames} from './metadata.utils';

import type {AssetSummaryDTO, BuyerAddedMetadata, TrustAccountActivityDTO} from '@sollapay/types';

interface BuyerAddedRowProps {
  activity: TrustAccountActivityDTO;
}

export function BuyerAddedRow({activity}: BuyerAddedRowProps) {
  const {t} = useTranslation('trustAccounts');
  const navigate = useNavigate();
  const {canCollectPayments} = useTrustAccountPermissions();
  const {allowsMultipleAssets} = useTrustTypeStrategy();

  const meta = castMetadata<BuyerAddedMetadata>(activity);
  const asset = readAssetSummary(activity);
  const buyerNames = readBuyerNames(meta);
  const partyId = activity.entityId;
  const title = t('view.activity.activityType.buyer_added_count', {
    count: meta?.buyerCount ?? buyerNames.length
  });

  const {data: instructions} = usePaymentsQuery(activity.trustAccountId);

  const purchaseId = meta?.purchaseId;
  const purchaseInstructions = purchaseId
    ? (instructions ?? []).filter(i => i.trustBuyerPurchaseId === purchaseId)
    : [];
  const showRequestPayment =
    canCollectPayments && !purchaseInstructions.some(isNonTerminalInstruction);

  const buyerPath = partyId
    ? `/trust-accounts/${activity.trustAccountId}/buyers/${partyId}${getBuyerLinkQuery(purchaseId, asset)}`
    : undefined;

  return (
    <ActivityFeedRowLayout
      icon="sollapay:buyer"
      title={title}
      subtitle={getAssetFeedSubtitle(asset, t, allowsMultipleAssets)}
      actorName={buyerNames.length > 0 ? <BuyerNamesSummary buyerNames={buyerNames} /> : null}
      createdAt={activity.createdAt}
      action={
        partyId ? (
          <BuyerPaymentButton
            trustAccountId={activity.trustAccountId}
            partyId={partyId}
            purchaseId={purchaseId}
            showRequestPayment={showRequestPayment}
            onViewBuyer={() => buyerPath && navigate(buyerPath)}
          />
        ) : undefined
      }
    />
  );
}

/** Activity rows that predate `purchaseId` can only name the purchase by its asset. */
function getBuyerLinkQuery(purchaseId: string | undefined, asset: AssetSummaryDTO | null): string {
  if (purchaseId) return `?${PURCHASE_PARAM}=${purchaseId}`;
  if (asset) return `?${LEGACY_ASSET_PARAM}=${asset.id}`;
  return '';
}
